import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AppUser } from '../models/appUser';
import { UserSectionAccess } from '../models/userSectionAccess';
import { PaginatedResponse } from '../models/paginatedResponse';
import { CreateUserRequest } from '../models/api/createUserRequest';
import { UpdateUserRequest } from '../models/api/updateUserRequest';
import { UpdateActiveStatusRequest } from '../models/api/updateActiveStatusRequest';
import { UpdateCountryAccessRequest } from '../models/api/updateCountryAccessRequest';
import { UpdateSectionAccessRequest } from '../models/api/updateSectionAccessRequest';
import { ChangePasswordRequest } from '../models/api/changePasswordRequest';
import { BulkDeleteResult } from '../models/api/bulkDeleteResult';
import { Category } from '../models/enums/category.enum';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private http = inject(HttpClient);
  private readonly API_BASE_URL = `${environment.apiUrl}/users`;

  getUsers(
    pageNumber: number = 1,
    pageSize: number = 10,
    sortBy: string = 'Id',
    ascending: boolean = true
  ): Observable<PaginatedResponse<AppUser>> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString())
      .set('sortBy', sortBy)
      .set('ascending', ascending.toString());

    return this.http.get<PaginatedResponse<AppUser>>(this.API_BASE_URL, { params });
  }

  getUser(id: string): Observable<AppUser> {
    return this.http.get<AppUser>(`${this.API_BASE_URL}/${id}`);
  }

  getCurrentUser(): Observable<AppUser> {
    return this.http.get<AppUser>(`${this.API_BASE_URL}/me`);
  }

  createUser(request: CreateUserRequest): Observable<AppUser> {
    return this.http.post<AppUser>(this.API_BASE_URL, request);
  }

  updateUser(id: string, request: UpdateUserRequest): Observable<AppUser> {
    return this.http.put<AppUser>(`${this.API_BASE_URL}/${id}`, request);
  }

  deleteUser(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/${id}`);
  }

  deleteUsers(ids: string[]): Observable<BulkDeleteResult> {
    return this.http.post<BulkDeleteResult>(`${this.API_BASE_URL}/bulk-delete`, { ids });
  }

  updateActiveStatus(id: string, request: UpdateActiveStatusRequest): Observable<AppUser> {
    return this.http.patch<AppUser>(`${this.API_BASE_URL}/${id}/active-status`, request);
  }

  updateCountryAccess(id: string, request: UpdateCountryAccessRequest): Observable<string[]> {
    return this.http.put<string[]>(`${this.API_BASE_URL}/${id}/country-access`, request);
  }

  getCountryAccess(id: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.API_BASE_URL}/${id}/country-access`);
  }

  getSectionAccess(id: string): Observable<UserSectionAccess[]> {
    return this.http.get<UserSectionAccess[]>(`${this.API_BASE_URL}/${id}/section-access`);
  }

  updateSectionAccess(id: string, request: UpdateSectionAccessRequest): Observable<UserSectionAccess[]> {
    return this.http.put<UserSectionAccess[]>(`${this.API_BASE_URL}/${id}/section-access`, request);
  }

  changePassword(request: ChangePasswordRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/me/change-password`, request);
  }

  getUsersByCategory(category: Category): Observable<AppUser[]> {
    return this.http.get<AppUser[]>(`${this.API_BASE_URL}/by-category/${category}`);
  }
}
